import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr, API } from "../../lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, X, Tag, Check, Pencil, Briefcase, Phone, FileDown, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../../components/ui/tooltip";
import { LateArrivalPanel } from "./LateArrivalPanel";

const CHAN_TIP = (b) => {
  if (b.status !== "blocked") return b.status === "demande" ? "En attente de paiement" : "Réservation directe (site)";
  const c = (b.beds24_channel || "").toLowerCase();
  if (b.beds24_is_block || c === "blocage") return "Blocage manuel";
  if (c === "airbnb") return "Airbnb";
  if (c.includes("booking")) return "Booking.com";
  if (c === "direct") return "Réservation directe (site)";
  return "Externe (canal)";
};

function BandTooltip({ b, children }) {
  const nights = b.arrivee && b.depart ? Math.round((new Date(b.depart) - new Date(b.arrivee)) / 86400000) : null;
  const isBlock = b.status === "blocked" && (b.beds24_is_block || (b.beds24_channel || "").toLowerCase() === "blocage");
  const clientName = !isBlock ? (b.guest_name || (b.status !== "blocked" ? b.nom : null)) : null;
  const motif = isBlock ? (b.guest_name || b.notes || "Indisponible") : null;
  const TipRow = ({ l, v }) => v || v === 0 ? (
    <div className="flex justify-between gap-4"><span className="text-white/50">{l}</span><span className="font-medium text-white">{v}</span></div>
  ) : null;
  return (
    <Tooltip delayDuration={80}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="top" className="bg-ink-900 text-white border-0 shadow-lg px-3 py-2.5 max-w-[240px]">
        <div className="space-y-1 text-[11px]" data-testid={`cal-tooltip-${b.id}`}>
          <TipRow l="Canal" v={CHAN_TIP(b)} />
          <TipRow l="Client" v={clientName} />
          <TipRow l="Montant" v={b.total != null ? `${Number(b.total).toFixed(0)} €` : null} />
          <TipRow l="Nuits" v={nights} />
          <TipRow l="Motif" v={motif} />
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const WD = ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."];
const iso = (d) => d.toISOString().slice(0, 10);

export default function CalendarTab({ onNewBooking }) {
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth(), d: today.getDate() });
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [edit, setEdit] = useState(null); // {accId, type:'price'|'min', day}
  const [selectedBooking, setSelectedBooking] = useState(null);

  const SPAN = 6; // mois affichés, vue glissante à partir de la date du curseur (aujourd'hui par défaut)
  const fmtd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  const startDate = new Date(cursor.y, cursor.m, cursor.d);
  const endDate = new Date(cursor.y, cursor.m + SPAN, cursor.d);
  const start = fmtd(startDate);
  const end = fmtd(endDate);
  const todayStr = fmtd(new Date());
  const days = [];
  for (let dt = new Date(startDate); dt < endDate; dt.setDate(dt.getDate() + 1)) days.push(fmtd(new Date(dt)));
  const monthGroups = [];
  days.forEach((d) => {
    const key = d.slice(0, 7);
    const g = monthGroups[monthGroups.length - 1];
    if (g && g.key === key) g.count++;
    else monthGroups.push({ key, count: 1, label: `${MONTHS[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}` });
  });
  const lastDay = new Date(endDate); lastDay.setDate(lastDay.getDate() - 1);

  const load = useCallback(() => {
    api.get(`/admin/calendar?start=${start}&end=${end}`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  const move = (delta) => {
    const nd = new Date(cursor.y, cursor.m + delta, cursor.d);
    setCursor({ y: nd.getFullYear(), m: nd.getMonth(), d: nd.getDate() });
  };
  const goToday = () => setCursor({ y: today.getFullYear(), m: today.getMonth(), d: today.getDate() });

  if (!data) return <p className="text-ink-400">Chargement…</p>;
  if (!data.accommodations?.length) return <p className="text-ink-400">Aucun logement.</p>;

  const rateFor = (a, day) => data.rates?.[a.id]?.[day] || {};
  const priceFor = (a, day) => { const r = rateFor(a, day); return r.price ?? a.price_from ?? null; };
  const minFor = (a, day) => { const r = rateFor(a, day); return r.min_nights ?? a.min_nights ?? null; };
  const bookingFor = (a, day) => (data.bookings || []).find((b) => b.accommodation_id === a.id && b.arrivee <= day && day < b.depart);
  const arrivingFor = (a, day) => (data.bookings || []).find((b) => b.accommodation_id === a.id && b.arrivee === day);
  const departingFor = (a, day) => (data.bookings || []).find((b) => b.accommodation_id === a.id && b.depart === day);
  const middleFor = (a, day) => (data.bookings || []).find((b) => b.accommodation_id === a.id && b.arrivee < day && day < b.depart);

  const saveCell = async (a, type, day, value) => {
    setEdit(null);
    if (value === "" || value === null) return;
    const num = Number(value);
    if (Number.isNaN(num)) return toast.error("Valeur invalide");
    const body = { accommodation_id: a.id, dates: [day] };
    body[type === "price" ? "price" : "min_nights"] = num;
    try { await api.put("/admin/rates", body); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const bColor = (s) => s === "demande" ? "bg-amber-400" : s === "blocked" ? "bg-ink-400" : "bg-[#e5806e]";
  const blkColor = (m) => {
    if (m.status !== "blocked") return bColor(m.status);
    const c = (m.beds24_channel || "").toLowerCase();
    if (m.beds24_is_block || c === "blocage") return "bg-slate-400";
    if (c === "airbnb") return "bg-rose-400";
    if (c.includes("booking")) return "bg-blue-500";
    if (c === "direct") return "bg-brand";
    return "bg-ink-400";
  };
  const cellW = "min-w-[52px] w-[52px]";
  const mstart = (d) => (d.slice(8) === "01" ? " border-l-2 border-l-brand/40" : "");
  const stickyTd = "sticky left-0 z-10 bg-white text-left text-xs font-medium text-ink-700 p-2 border-b border-r border-black/5 whitespace-nowrap";

  return (
    <TooltipProvider delayDuration={80}>
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => move(-1)} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="cal-prev"><ChevronLeft size={18} /></button>
          <span className="text-base sm:text-lg font-bold tracking-tight text-slate-900 min-w-[240px] text-center" data-testid="cal-range-label">{startDate.getDate()} {MONTHS[startDate.getMonth()]} → {lastDay.getDate()} {MONTHS[lastDay.getMonth()]} {lastDay.getFullYear()}</span>
          <button onClick={() => move(1)} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="cal-next"><ChevronRight size={18} /></button>
          <button onClick={goToday} className="ml-1 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50" data-testid="cal-today">Aujourd'hui</button>
        </div>
        <button onClick={() => setPricing({ accId: null, start: days[0], end: days[days.length - 1] })} className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-brand text-white px-4 py-2 text-sm font-medium hover:bg-brand-600" data-testid="cal-pricing-btn"><Tag size={14} /> Tarifs et séjour</button>
        {onNewBooking && (
          <button onClick={onNewBooking} className="inline-flex items-center gap-1.5 rounded-full border border-brand text-brand px-4 py-2 text-sm font-medium hover:bg-brand-50" data-testid="cal-new-booking-btn"><Phone size={14} /> Nouvelle réservation</button>
        )}
      </div>

      <div className="space-y-6">
        {data.accommodations.map((acc) => (
          <div key={acc.id} className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white" data-testid={`cal-acc-block-${acc.id}`}>
            <table className="border-collapse text-center w-full">
              <thead>
                <tr>
                  <th rowSpan={2} className="sticky left-0 z-10 bg-white text-left text-xs font-semibold text-brand p-3 min-w-[150px] border-b border-r border-black/5">
                    {acc.name}<span className="block text-[10px] text-ink-400 font-normal capitalize">{acc.type}</span>
                  </th>
                  {monthGroups.map((g) => (
                    <th key={g.key} colSpan={g.count} className="p-1.5 border-b border-l-2 border-l-brand/40 border-black/5 text-[11px] font-bold text-brand bg-brand/5 text-center whitespace-nowrap">{g.label}</th>
                  ))}
                </tr>
                <tr>
                  {days.map((d) => {
                    const dd = new Date(d);
                    const we = dd.getDay() === 0 || dd.getDay() === 6;
                    const isToday = d === todayStr;
                    return <th key={d} className={`${cellW} p-1 border-b border-l border-black/5 text-[11px] font-medium ${isToday ? "bg-brand/10 ring-1 ring-inset ring-brand/40" : ""} ${we ? "text-brand" : "text-ink-600"}${mstart(d)}`}>{WD[dd.getDay()]}<br /><span className={isToday ? "text-brand font-bold" : "text-ink-900"}>{d.slice(8)}</span></th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {/* Tarif journalier */}
                <tr>
                  <td className={stickyTd}><span className="inline-flex items-center gap-1">Tarif / nuit <Pencil size={11} className="text-brand" /></span></td>
                  {days.map((day) => {
                    const b = bookingFor(acc, day);
                    const p = priceFor(acc, day);
                    const editing = edit?.accId === acc.id && edit?.type === "price" && edit?.day === day;
                    return (
                      <td key={day} className={`${cellW} border-b border-l border-black/5 p-0 ${b ? "bg-[#7fae3a]/60" : "bg-[#84b53f]"}${mstart(day)} `} data-testid={`cal-price-${acc.id}-${day}`}>
                        {editing ? (
                          <input autoFocus type="number" defaultValue={p ?? ""} onBlur={(e) => saveCell(acc, "price", day, e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveCell(acc, "price", day, e.target.value)}
                            className="w-full text-center text-xs py-2 outline-none bg-white text-ink-900" />
                        ) : (
                          <button onClick={() => setEdit({ accId: acc.id, type: "price", day })} className="w-full py-2 text-xs font-medium text-white hover:bg-black/10 transition-colors" title="Cliquer pour modifier le tarif">{p ? p.toFixed(0) : "—"}</button>
                        )}
                      </td>
                    );
                  })}
                </tr>
                {/* Séjour minimum */}
                <tr>
                  <td className={stickyTd}>Séjour min.</td>
                  {days.map((day) => {
                    const editing = edit?.accId === acc.id && edit?.type === "min" && edit?.day === day;
                    const mn = minFor(acc, day);
                    return (
                      <td key={day} className={`${cellW} border-b border-l border-black/5 p-0 bg-cream`} data-testid={`cal-min-${acc.id}-${day}`}>
                        {editing ? (
                          <input autoFocus type="number" min="1" defaultValue={mn ?? ""} onBlur={(e) => saveCell(acc, "min", day, e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveCell(acc, "min", day, e.target.value)}
                            className="w-full text-center text-xs py-2 outline-none bg-white text-ink-900" />
                        ) : (
                          <button onClick={() => setEdit({ accId: acc.id, type: "min", day })} className="w-full py-2 text-xs text-ink-600 hover:bg-black/5" title="Cliquer pour modifier le séjour minimum">{mn ?? "—"}</button>
                        )}
                      </td>
                    );
                  })}
                </tr>
                {/* Réserver (valise) */}
                <tr>
                  <td className={stickyTd}>Réserver</td>
                  {days.map((day) => {
                    const b = bookingFor(acc, day);
                    return (
                      <td key={day} className={`${cellW} border-b border-l border-black/5 py-1.5 ${b ? "bg-black/5" : ""}${mstart(day)}`}>
                        {!b && (
                          <button onClick={() => setDialog({ accommodation: acc, arrivee: day })} className="mx-auto flex items-center justify-center text-ink-400 hover:text-brand" title="Réserver en direct" data-testid={`cal-book-${acc.id}-${day}`}>
                            <Briefcase size={15} />
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
                {/* Bandeau réservations */}
                <tr>
                  <td className="sticky left-0 z-10 bg-white text-left text-xs font-medium text-ink-700 p-2 border-r border-black/5 whitespace-nowrap">Occupation</td>
                  {days.map((day) => {
                    const mid = middleFor(acc, day);
                    const arr = arrivingFor(acc, day);
                    const dep = departingFor(acc, day);
                    const open = (b) => setSelectedBooking({ ...b, accommodation_name: acc.name, accommodation_type: acc.type });
                    const canOpen = (b) => b.status !== "blocked" || !(b.beds24_is_block || (b.beds24_channel || "").toLowerCase() === "blocage");
                    // Full-cell night (guest is staying over this whole day)
                    if (mid) {
                      const clickable = canOpen(mid);
                      return (
                        <td key={day} className={`${cellW} p-0`}>
                          <BandTooltip b={mid}>
                            <div onClick={() => clickable && open(mid)} className={`${blkColor(mid)} text-white text-[10px] py-1.5 px-1 h-[26px] ${clickable ? "cursor-pointer hover:opacity-90" : ""}`} />
                          </BandTooltip>
                        </td>
                      );
                    }
                    // Changeover-friendly half cells: departure = left half, arrival = right half
                    if (arr || dep) {
                      return (
                        <td key={day} className={`${cellW} p-0`}>
                          <div className="flex h-[26px]">
                            <div className="w-1/2 h-full">
                              {dep && (
                                <BandTooltip b={dep}>
                                  <div onClick={() => canOpen(dep) && open(dep)} className={`${blkColor(dep)} h-full rounded-r-full ${canOpen(dep) ? "cursor-pointer hover:opacity-90" : ""}`} />
                                </BandTooltip>
                              )}
                            </div>
                            <div className="w-1/2 h-full relative">
                              {arr && (
                                <BandTooltip b={arr}>
                                  <div
                                    onClick={() => canOpen(arr) && open(arr)}
                                    className={`${blkColor(arr)} h-full rounded-l-full ${canOpen(arr) ? "cursor-pointer hover:opacity-90" : ""}`}
                                    data-testid={`cal-booking-${arr.id}`}
                                  >
                                    <span className="pointer-events-none absolute left-1 top-0 leading-[26px] text-[10px] text-white whitespace-nowrap">{arr.nom || (arr.status === "blocked" ? "Bloqué" : arr.status)}</span>
                                  </div>
                                </BandTooltip>
                              )}
                            </div>
                          </div>
                        </td>
                      );
                    }
                    return <td key={day} className={`${cellW} p-0`} />;
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <p className="text-xs text-ink-400 mt-3">Couleurs des réservations : <span className="font-semibold text-rose-500">Airbnb</span> · <span className="font-semibold text-blue-600">Booking.com</span> · <span className="font-semibold text-slate-500">Blocage manuel</span> · <span className="font-semibold text-[#e5806e]">réservation directe (site)</span>. Cliquez la bande verte pour le tarif à la nuit · « Séjour min. » = nuits minimum · « Tarifs par période » pour appliquer un prix à plusieurs jours/logements en une fois.</p>

      <ManualBookingDialog dialog={dialog} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />
      <PricingDialog pricing={pricing} accommodations={data.accommodations} onClose={() => setPricing(null)} onSaved={() => { setPricing(null); load(); }} />
      <BookingDetailsDialog booking={selectedBooking} onClose={() => setSelectedBooking(null)} onRefunded={() => { setSelectedBooking(null); load(); }} onUpdated={load} />
    </div>
    </TooltipProvider>
  );
}

function PricingDialog({ pricing, accommodations, onClose, onSaved }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [price, setPrice] = useState("");
  const [minNights, setMinNights] = useState("");
  const [ids, setIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (pricing) { setFrom(pricing.start); setTo(pricing.end); setPrice(""); setMinNights(""); setIds(pricing.accId ? [pricing.accId] : []); }
  }, [pricing]);

  const toggle = (id) => setIds((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));
  const allChecked = accommodations && ids.length === accommodations.length;
  const toggleAll = () => setIds(allChecked ? [] : accommodations.map((a) => a.id));
  const rangeDates = () => { const out = []; for (let d = new Date(from); d <= new Date(to); d.setDate(d.getDate() + 1)) out.push(d.toISOString().slice(0, 10)); return out; };

  const apply = async () => {
    const hasPrice = price !== "";
    const hasMin = minNights !== "";
    if (!hasPrice && !hasMin) return toast.error("Saisissez un tarif et/ou un séjour minimum");
    const pr = Number(price), mn = Number(minNights);
    if (hasPrice && Number.isNaN(pr)) return toast.error("Tarif invalide");
    if (hasMin && (Number.isNaN(mn) || mn < 1)) return toast.error("Séjour minimum invalide");
    if (!ids.length) return toast.error("Choisissez au moins un logement");
    if (!from || !to || from > to) return toast.error("Période invalide");
    setSaving(true);
    try {
      const dates = rangeDates();
      const body = { dates };
      if (hasPrice) body.price = pr;
      if (hasMin) body.min_nights = mn;
      for (const id of ids) await api.put("/admin/rates", { accommodation_id: id, ...body });
      const parts = [hasPrice && "tarif", hasMin && "séjour min."].filter(Boolean).join(" et ");
      toast.success(`${parts.charAt(0).toUpperCase() + parts.slice(1)} appliqué à ${ids.length} logement(s) sur ${dates.length} nuit(s)`);
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const input = "w-full rounded-lg border border-ink-400/25 px-3 py-2 text-sm outline-none focus:border-brand";
  const label = "text-xs font-medium text-ink-600 mb-1 block";

  return (
    <Dialog open={!!pricing} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-cream max-w-lg max-h-[85vh] overflow-y-auto" data-testid="pricing-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Tarifs et séjour par période</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Du</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={input} data-testid="pricing-from" /></div>
            <div><label className={label}>Au (inclus)</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={input} data-testid="pricing-to" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Tarif à la nuit (€)</label><input type="number" min="0" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="ex : 95" className={input} data-testid="pricing-price" autoFocus /></div>
            <div><label className={label}>Séjour minimum (nuits)</label><input type="number" min="1" value={minNights} onChange={(e) => setMinNights(e.target.value)} placeholder="ex : 2" className={input} data-testid="pricing-min-nights" /></div>
          </div>
          <p className="text-[11px] text-ink-400 -mt-2">Renseignez le tarif, le séjour minimum, ou les deux : seuls les champs remplis sont appliqués.</p>
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-ink-600">Appliquer aux logements</span>
              <button onClick={toggleAll} className="text-xs text-brand font-medium" data-testid="pricing-all">{allChecked ? "Tout décocher" : "Tout sélectionner"}</button>
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto">
              {accommodations.map((a) => (
                <button type="button" key={a.id} onClick={() => toggle(a.id)} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-left transition-colors ${ids.includes(a.id) ? "border-brand bg-brand-50 text-brand" : "border-ink-400/25 text-ink-600"}`} data-testid={`pricing-acc-${a.id}`}>
                  <span className={`w-4 h-4 rounded flex items-center justify-center ${ids.includes(a.id) ? "bg-brand text-white" : "border border-ink-400/40"}`}>{ids.includes(a.id) && <Check size={12} />}</span>
                  <span className="truncate">{a.name}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={apply} disabled={saving} className="flex-1 rounded-full bg-brand text-white py-2.5 font-medium hover:bg-brand-600 disabled:opacity-60" data-testid="pricing-apply">{saving ? "Application…" : "Appliquer"}</button>
            <button onClick={onClose} className="rounded-full border border-ink-400/30 text-ink-600 px-6 py-2.5"><X size={16} /></button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function fmtDate(s) {
  if (!s) return "—";
  const [y, m, d] = s.slice(0, 10).split("-");
  return `${d}-${m}-${y}`;
}
function euro(v) { return (v || v === 0) ? `${Number(v).toFixed(2)} €` : "—"; }
const STATUS_LABEL = { "confirmée": "Confirmée", manual: "Réservation directe", en_attente_paiement: "En attente de paiement", demande: "En attente de paiement", blocked: "Externe (canal)", no_show: "Non-présentation", expirée: "Expirée", "annulée": "Annulée" };
const SOURCE_LABEL = { en_ligne: "Réservation en ligne", website: "Site internet", telephone: "Téléphone", beds24: "Beds24 / plateforme" };
const PAY_STATUS = {
  paid: { label: "Acompte réglé en ligne", cls: "bg-emerald-50 text-emerald-700" },
  initiated: { label: "En attente de paiement", cls: "bg-amber-50 text-amber-700" },
  sur_place: { label: "Paiement sur place", cls: "bg-brand-50 text-brand" },
  expiré: { label: "Lien expiré", cls: "bg-red-50 text-red-600" },
};

function GuestEditor({ b, onSaved, onCancel }) {
  const [f, setF] = useState({ prenom: b.prenom || "", nom: b.nom || "", telephone: b.telephone || "", email: b.email || "" });
  const [saving, setSaving] = useState(false);
  const inp = "w-full rounded-lg border border-ink-400/25 px-3 py-2 text-sm";
  const save = async () => {
    if (!f.nom.trim()) return toast.error("Le nom est requis");
    if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return toast.error("E-mail invalide");
    setSaving(true);
    try {
      const payload = { prenom: f.prenom.trim(), nom: f.nom.trim(), telephone: f.telephone.trim(), email: f.email.trim().toLowerCase(), guest_edited: true };
      await api.put(`/admin/bookings/${b.id}`, payload);
      toast.success("Coordonnées du client mises à jour");
      onSaved(payload);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-2" data-testid="bd-guest-editor">
      <input className={inp} placeholder="Prénom" value={f.prenom} onChange={(e) => setF({ ...f, prenom: e.target.value })} data-testid="bd-edit-prenom" />
      <input className={inp} placeholder="Nom" value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} data-testid="bd-edit-nom" />
      <input className={inp} placeholder="Téléphone" value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })} data-testid="bd-edit-telephone" />
      <input className={inp} type="email" placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="bd-edit-email" />
      <div className="flex gap-2">
        <button onClick={save} disabled={saving} className="flex-1 rounded-full bg-brand text-white py-2 text-sm font-medium hover:bg-brand-600 disabled:opacity-60" data-testid="bd-edit-save">{saving ? "Enregistrement…" : "Enregistrer"}</button>
        <button onClick={onCancel} className="rounded-full border border-ink-400/30 text-ink-600 px-4 py-2 text-sm" data-testid="bd-edit-cancel">Annuler</button>
      </div>
    </div>
  );
}

export function BookingDetailsDialog({ booking, onClose, onRefunded, onUpdated }) {
  const [guest, setGuest] = useState(null);
  const [editingGuest, setEditingGuest] = useState(false);
  const b = booking ? { ...booking, ...(guest || {}) } : booking;
  const fullName = b ? `${b.prenom || ""} ${b.nom || ""}`.trim() : "";
  const [refundAmt, setRefundAmt] = useState("");
  const [refunding, setRefunding] = useState(false);
  const [noshowAmt, setNoshowAmt] = useState("");
  const [charging, setCharging] = useState(false);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [settling, setSettling] = useState(false);
  const [resending, setResending] = useState("");
  useEffect(() => { setRefundAmt(""); setNoshowAmt(""); setNotes(booking?.notes || ""); setGuest(null); setEditingGuest(false); }, [booking]);
  const nights = b?.arrivee && b?.depart ? Math.round((new Date(b.depart) - new Date(b.arrivee)) / 86400000) : null;
  const canRefund = b && b.session_id && (b.payment_status === "paid" || (b.paid_amount || 0) > 0);
  const canChargeNoShow = b && b.stripe_customer_id && b.stripe_payment_method_id && !(b.noshow_charged_amount > 0);
  const alreadyRefunded = Number(b?.refunded_amount || 0);
  const isPaid = b && (b.payment_status === "paid" || (b.paid_amount || 0) > 0);
  const acompteRegle = b ? (b.paid_amount != null ? b.paid_amount : (isPaid ? b.deposit : 0)) : 0;
  const reste = b ? (b.balance != null ? b.balance : (b.total != null ? Math.max(b.total - (b.deposit || 0), 0) : null)) : null;
  const payInfo = b ? (PAY_STATUS[b.payment_status] || null) : null;

  const doRefund = async () => {
    const amt = refundAmt === "" ? null : Number(refundAmt);
    if (amt !== null && (Number.isNaN(amt) || amt <= 0)) return toast.error("Montant invalide");
    if (!window.confirm(amt === null ? "Rembourser la totalité du paiement en ligne ?" : `Rembourser ${amt.toFixed(2)} € ?`)) return;
    setRefunding(true);
    try {
      const r = await api.post(`/admin/bookings/${b.id}/refund`, { amount: amt });
      toast.success(`Remboursement de ${r.data.amount.toFixed(2)} € effectué (${r.data.status})`);
      onRefunded?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setRefunding(false); }
  };

  const doChargeNoShow = async () => {
    const amt = noshowAmt === "" ? null : Number(noshowAmt);
    if (amt !== null && (Number.isNaN(amt) || amt <= 0)) return toast.error("Montant invalide");
    const label = amt === null ? "le solde du séjour (70 %)" : `${amt.toFixed(2)} €`;
    if (!window.confirm(`Prélever ${label} sur la carte du client pour non-présentation (no-show) ? Cette action est irréversible.`)) return;
    setCharging(true);
    try {
      const r = await api.post(`/admin/bookings/${b.id}/charge-noshow`, { amount: amt });
      toast.success(`Prélèvement de ${r.data.amount.toFixed(2)} € effectué (${r.data.status})`);
      onRefunded?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setCharging(false); }
  };

  const doMarkBalancePaid = async (method) => {
    if (!window.confirm(`Confirmer : le solde de ${euro(reste)} a été payé sur place en ${method === "card" ? "carte bancaire" : "espèces"} ?`)) return;
    setSettling(true);
    try {
      await api.post(`/admin/bookings/${b.id}/mark-balance-paid`, { method });
      toast.success("Solde marqué comme payé. Facture entièrement acquittée disponible.");
      onRefunded?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setSettling(false); }
  };

  const downloadInvoice = async () => {
    try {
      const r = await api.get(`/bookings/${b.id}/facture.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `facture-${(b.nom || b.id).toString().replace(/\s+/g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Facture indisponible : l'acompte doit être réglé."); }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const url = b.source === "beds24" ? `/admin/bookings/${b.id}/block-note` : `/admin/bookings/${b.id}`;
      await api.put(url, { notes });
      toast.success("Note enregistrée");
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingNotes(false); }
  };

  const resend = async (kind) => {
    setResending(kind);
    try {
      const r = await api.post(`/admin/bookings/${b.id}/resend-${kind}`, {});
      toast.success(`E-mail envoyé à ${r.data.sent_to}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setResending(""); }
  };

  const Row = ({ label, value, testid }) => (
    <div className="flex justify-between gap-4 py-2 border-b border-black/5 last:border-0">
      <span className="text-xs text-ink-400">{label}</span>
      <span className="text-sm font-medium text-ink-900 text-right" data-testid={testid}>{value}</span>
    </div>
  );
  return (
    <Dialog open={!!b} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-cream max-w-md max-h-[85vh] overflow-y-auto" data-testid="booking-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{fullName || "Réservation"}</DialogTitle>
          <DialogDescription className="text-xs text-ink-400">Détail de la réservation</DialogDescription>
        </DialogHeader>
        {b && (
          <div className="space-y-4">
            <div className="rounded-xl bg-white p-4">
              <Row label="Logement" value={b.accommodation_name || "—"} testid="bd-accommodation" />
              <Row label="Statut" value={STATUS_LABEL[b.status] || b.status} testid="bd-status" />
              <Row label="Source" value={SOURCE_LABEL[b.source] || "—"} testid="bd-source" />
            </div>
            <div className="rounded-xl bg-white p-4">
              <Row label="Arrivée" value={fmtDate(b.arrivee)} testid="bd-arrivee" />
              <Row label="Départ" value={fmtDate(b.depart)} testid="bd-depart" />
              <Row label="Nuits" value={nights ?? "—"} testid="bd-nights" />
              <Row label="Voyageurs" value={b.voyageurs ?? "—"} testid="bd-voyageurs" />
            </div>
            <div className="rounded-xl bg-white p-4">
              {editingGuest ? (
                <GuestEditor b={b} onSaved={(p) => { setGuest(p); setEditingGuest(false); onUpdated?.(); }} onCancel={() => setEditingGuest(false)} />
              ) : (
                <div onClick={(e) => e.detail === 3 && setEditingGuest(true)} className="select-none" data-testid="bd-guest-block">
                  <Row label="Client" value={fullName || "—"} testid="bd-nom" />
                  <Row label="Téléphone" value={b.telephone || "—"} testid="bd-telephone" />
                  <Row label="E-mail" value={b.email || "—"} testid="bd-email" />
                </div>
              )}
            </div>
            <div className="rounded-xl bg-white p-4" data-testid="bd-payment">
              {payInfo && (
                <div className="flex justify-between items-center pb-2 mb-1 border-b border-black/5">
                  <span className="text-xs text-ink-400">Paiement</span>
                  <span className={`text-[11px] font-medium rounded-full px-2 py-0.5 ${payInfo.cls}`} data-testid="bd-pay-status">{payInfo.label}</span>
                </div>
              )}
              <Row label="Total du séjour" value={euro(b.total)} testid="bd-total" />
              <Row label={isPaid ? `Acompte réglé${b.confirmed_at ? " le " + fmtDate(b.confirmed_at) : ""}` : "Acompte"} value={euro(isPaid ? acompteRegle : b.deposit)} testid="bd-deposit" />
              <Row label={b.balance_paid ? "Solde (payé sur place)" : "Solde restant (70 %) — sur place ou no-show"} value={reste != null ? euro(reste) : "—"} testid="bd-balance" />
              {b.balance_paid && (
                <Row label={`Solde payé sur place${b.balance_paid_at ? " le " + fmtDate(b.balance_paid_at) : ""} (${b.balance_paid_method === "card" ? "CB" : "espèces"})`} value={euro(reste)} testid="bd-balance-paid" />
              )}
              {b.tourist_tax != null && <Row label="Taxe de séjour (à percevoir sur place)" value={euro(b.tourist_tax)} testid="bd-tourist-tax" />}
              <Row label="Caution (à l'arrivée)" value={euro(b.caution)} testid="bd-caution" />
              {alreadyRefunded > 0 && <Row label="Déjà remboursé" value={euro(alreadyRefunded)} testid="bd-refunded" />}
            </div>
            {isPaid && (
              <button onClick={downloadInvoice} data-testid="bd-invoice-btn" className="w-full rounded-full border border-brand text-brand py-2.5 text-sm font-medium hover:bg-brand-50 flex items-center justify-center gap-2">
                <FileDown size={16} /> Télécharger la facture (PDF)
              </button>
            )}
            {isPaid && !b.balance_paid && (reste || 0) > 0 && (
              <div className="rounded-xl bg-white p-4" data-testid="bd-settle-section">
                <span className="text-xs text-ink-400 block mb-1">Solde payé sur place ?</span>
                <p className="text-[11px] text-ink-400 mb-3">Enregistre le paiement du solde ({euro(reste)}) et rend la facture entièrement acquittée.</p>
                <div className="flex gap-2">
                  <button onClick={() => doMarkBalancePaid("cash")} disabled={settling} className="flex-1 rounded-full bg-emerald-600 text-white py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60" data-testid="bd-settle-cash">
                    {settling ? "…" : "Payé en espèces"}
                  </button>
                  <button onClick={() => doMarkBalancePaid("card")} disabled={settling} className="flex-1 rounded-full bg-emerald-600 text-white py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60" data-testid="bd-settle-card">
                    {settling ? "…" : "Payé en CB"}
                  </button>
                </div>
              </div>
            )}
            {canRefund && (
              <div className="rounded-xl bg-white p-4" data-testid="bd-refund-section">
                <span className="text-xs text-ink-400 block mb-2">Remboursement Stripe</span>
                <div className="flex gap-2">
                  <input
                    type="number" min="0" step="0.01" value={refundAmt}
                    onChange={(e) => setRefundAmt(e.target.value)}
                    placeholder="Total si vide"
                    className="flex-1 rounded-lg border border-ink-400/25 px-3 py-2 text-sm outline-none focus:border-brand"
                    data-testid="bd-refund-amount"
                  />
                  <button onClick={doRefund} disabled={refunding} className="rounded-full bg-[#e5806e] text-white px-5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60" data-testid="bd-refund-btn">
                    {refunding ? "…" : "Rembourser"}
                  </button>
                </div>
                <p className="text-[11px] text-ink-400 mt-2">Laissez vide pour un remboursement total, ou saisissez un montant partiel (€).</p>
              </div>
            )}
            {b.noshow_charged_amount > 0 && (
              <Row label={`Prélèvement no-show${b.noshow_charged_at ? " le " + fmtDate(b.noshow_charged_at) : ""}`} value={euro(b.noshow_charged_amount)} testid="bd-noshow-charged" />
            )}
            {canChargeNoShow && (
              <div className="rounded-xl bg-white p-4" data-testid="bd-noshow-section">
                <span className="text-xs text-ink-400 block mb-2">Prélèvement no-show (carte enregistrée)</span>
                <div className="flex gap-2">
                  <input
                    type="number" min="0" step="0.01" value={noshowAmt}
                    onChange={(e) => setNoshowAmt(e.target.value)}
                    placeholder="Solde 70 % si vide"
                    className="flex-1 rounded-lg border border-ink-400/25 px-3 py-2 text-sm outline-none focus:border-brand"
                    data-testid="bd-noshow-amount"
                  />
                  <button onClick={doChargeNoShow} disabled={charging} className="rounded-full bg-[#b3803a] text-white px-5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60" data-testid="bd-noshow-btn">
                    {charging ? "…" : "Prélever"}
                  </button>
                </div>
                <p className="text-[11px] text-ink-400 mt-2">Le client ne s'est pas présenté : prélève le solde sur la carte conservée lors de la réservation.</p>
              </div>
            )}
            {!(b.status === "blocked" && b.beds24_is_block) && <LateArrivalPanel b={b} />}
            <div className="rounded-xl bg-white p-4" data-testid="bd-notes-section">
              <span className="text-xs text-ink-400 block mb-2">Note interne (allergies, heure d'arrivée, demandes…)</span>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                className="w-full rounded-lg border border-ink-400/25 px-3 py-2 text-sm outline-none focus:border-brand resize-y"
                placeholder="Visible uniquement en interne" data-testid="bd-notes-input" />
              <button onClick={saveNotes} disabled={savingNotes} className="mt-2 rounded-full bg-ink-900 text-white px-4 py-1.5 text-xs font-medium hover:opacity-90 disabled:opacity-60" data-testid="bd-notes-save">
                {savingNotes ? "…" : "Enregistrer la note"}
              </button>
            </div>
            <div className="rounded-xl bg-white p-4" data-testid="bd-resend-section">
              <span className="text-xs text-ink-400 block mb-2">Renvoyer un e-mail au client</span>
              <div className="flex flex-wrap gap-2 items-center">
                {b.email && (
                  <button onClick={() => resend("confirmation")} disabled={resending === "confirmation"} className="rounded-full border border-brand text-brand px-4 py-1.5 text-xs font-medium hover:bg-brand-50 disabled:opacity-60 flex items-center gap-1.5" data-testid="bd-resend-confirmation">
                    <Mail size={13} /> {resending === "confirmation" ? "…" : "Confirmation"}
                  </button>
                )}
                {b.payment_url && !isPaid && (
                  <button onClick={() => resend("payment-link")} disabled={resending === "payment-link"} className="rounded-full border border-brand text-brand px-4 py-1.5 text-xs font-medium hover:bg-brand-50 disabled:opacity-60 flex items-center gap-1.5" data-testid="bd-resend-payment">
                    <Mail size={13} /> {resending === "payment-link" ? "…" : "Lien de paiement"}
                  </button>
                )}
                {!b.email && <span className="text-[11px] text-ink-400">Aucune adresse e-mail sur cette réservation.</span>}
              </div>
            </div>
            <p className="text-[11px] text-ink-400">Les informations de carte bancaire ne sont pas conservées : le paiement est géré de façon sécurisée par Stripe (conformité PCI).</p>
            <button onClick={onClose} className="w-full rounded-full bg-brand text-white py-2.5 font-medium hover:bg-brand-600" data-testid="bd-close">Fermer</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ManualBookingDialog({ dialog, onClose, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (dialog) {
      const next = new Date(dialog.arrivee); next.setDate(next.getDate() + 1);
      setForm({ arrivee: dialog.arrivee, depart: next.toISOString().slice(0, 10), voyageurs: 2, status: "manual", nom: "", email: "", telephone: "", total: "", deposit: "", caution: "", notes: "" });
    }
  }, [dialog]);
  const s = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const input = "w-full rounded-lg border border-ink-400/25 px-3 py-2 text-sm outline-none focus:border-brand";
  const label = "text-xs font-medium text-ink-600 mb-1 block";

  const save = async () => {
    try {
      await api.post("/admin/manual-booking", {
        accommodation_id: dialog.accommodation.id, accommodation_name: dialog.accommodation.name,
        ...form, voyageurs: Number(form.voyageurs) || 1,
        total: form.total ? Number(form.total) : null, deposit: form.deposit ? Number(form.deposit) : null, caution: form.caution ? Number(form.caution) : null,
      });
      toast.success("Réservation enregistrée");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={!!dialog} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-cream max-w-lg max-h-[85vh] overflow-y-auto" data-testid="manual-booking-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Réservation · {dialog?.accommodation?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Arrivée</label><input type="date" value={form.arrivee || ""} onChange={s("arrivee")} className={input} data-testid="mb-arrivee" /></div>
            <div><label className={label}>Départ</label><input type="date" value={form.depart || ""} onChange={s("depart")} className={input} data-testid="mb-depart" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Client</label><input value={form.nom || ""} onChange={s("nom")} className={input} data-testid="mb-nom" /></div>
            <div><label className={label}>Voyageurs</label><input type="number" min="1" value={form.voyageurs || 1} onChange={s("voyageurs")} className={input} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>E-mail</label><input value={form.email || ""} onChange={s("email")} className={input} /></div>
            <div><label className={label}>Téléphone</label><input value={form.telephone || ""} onChange={s("telephone")} className={input} /></div>
          </div>
          <div>
            <label className={label}>Statut</label>
            <select value={form.status || "manual"} onChange={s("status")} className={input} data-testid="mb-status">
              <option value="manual">Réservation manuelle</option>
              <option value="confirmée">Confirmée</option>
              <option value="blocked">Bloquer (indisponible)</option>
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className={label}>Total (€)</label><input type="number" value={form.total || ""} onChange={s("total")} className={input} /></div>
            <div><label className={label}>Acompte (€)</label><input type="number" value={form.deposit || ""} onChange={s("deposit")} className={input} /></div>
            <div><label className={label}>Caution (€)</label><input type="number" value={form.caution || ""} onChange={s("caution")} className={input} /></div>
          </div>
          <div><label className={label}>Notes</label><textarea rows={2} value={form.notes || ""} onChange={s("notes")} className={input} /></div>
          <div className="flex gap-3 pt-1">
            <button onClick={save} className="flex-1 rounded-full bg-brand text-white py-2.5 font-medium hover:bg-brand-600" data-testid="mb-save">Enregistrer</button>
            <button onClick={onClose} className="rounded-full border border-ink-400/30 text-ink-600 px-6 py-2.5"><X size={16} /></button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
